import { useIntl } from '@umijs/max';
import { createStyles } from 'antd-style';
import { useVideoPlayback } from '../hooks/useVideoPlayback';
import { useMediaProgress } from '../hooks/useMediaProgress';
import MediaTime from './MediaTime';
import {
  type CSSProperties,
  memo,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FullscreenOutlined,
  CompressOutlined,
  AudioMutedOutlined,
  CameraOutlined,
  PauseOutlined,
  PlaySquareOutlined,
  SoundOutlined,
  VideoCameraOutlined,
} from './icon/tabler';

type VideoMetaCacheEntry = {
  duration?: number;
  width?: number;
  height?: number;
};

const videoMetaCache = new Map<string, VideoMetaCacheEntry>();

function rememberVideoMeta(
  src: string,
  patch: VideoMetaCacheEntry,
): VideoMetaCacheEntry {
  const prev = videoMetaCache.get(src) ?? {};
  const next = { ...prev, ...patch };
  videoMetaCache.set(src, next);
  return next;
}

export interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  interactionLight?: boolean;
  shouldHandleSpace?: (event: KeyboardEvent) => boolean;
  allowFullscreen?: boolean;
  preview?: boolean;
  // Only focused overlays reserve playback priority; embedded previews do not.
  exclusive?: boolean;
  maxHeight?: number;
  onError?: () => void;
  poster?: string;
  deferVideoThumb?: boolean;
  videoElRef?: RefObject<HTMLVideoElement | null>;
  disabled?: boolean;
  onDurationChange?: (durationSec: number) => void;
  onDimensions?: (w: number, h: number) => void;
  onMountedChange?: (mounted: boolean) => void;
  // 截帧:右上角按钮,点了把当前播放秒数交给父节点抓帧(父节点读 videoElRef.currentTime)。
  onCaptureFrame?: () => void;
  captureBusy?: boolean;
  style?: CSSProperties;
  className?: string;
}

const useStyles = createStyles(({ token, css }) => ({
  wrap: css`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #000;
    contain: layout style paint;
    &[data-preview='true'] video, &[data-preview='true'] img { object-fit: contain; }
    video {
      /* 绝对定位:让 DOM 中靠后的播放 video 叠在缩略图 video 之上;
         否则非定位的播放 video 会被定位的缩略图盖住,hover 永远看到冻住的首帧。 */
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      background: #000;
      outline: none;
      opacity: 0;
      transition: opacity 180ms ease-out;
      &[data-ready='true'] {
        opacity: 1;
      }
    }
  `,
  muteChip: css`
    position: absolute;
    top: 6px;
    left: 6px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    transition:
      opacity 160ms ease-out,
      background 120ms ease-out;
    z-index: 2;
    opacity: 0;
    pointer-events: none;
    &[data-show='true'] {
      opacity: 1;
      pointer-events: auto;
    }
    &:hover {
      background: rgba(0, 0, 0, 0.78);
    }
  `,
  captureChip: css`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    transition:
      opacity 160ms ease-out,
      background 120ms ease-out;
    z-index: 2;
    opacity: 0;
    pointer-events: none;
    &[data-show='true'] {
      opacity: 1;
      pointer-events: auto;
    }
    &:hover {
      background: rgba(0, 0, 0, 0.78);
    }
    &[data-busy='true'] {
      cursor: default;
      opacity: 0.6;
    }
  `,
  chrome: css`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 18px 8px 6px;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.62) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    display: flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-size: 11px;
    opacity: 0;
    transition: opacity 160ms ease-out;
    pointer-events: none;
    z-index: 2;
    &[data-show='true'] {
      opacity: 1;
      pointer-events: auto;
    }
  `,
  playBtn: css`
    border: 0;
    padding: 0;
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 120ms ease-out;
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  `,
  progressTrack: css`
    flex: 1;
    height: 14px;
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    touch-action: none;
  `,
  progressLine: css`
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    position: relative;
    overflow: hidden;
  `,
  progressFill: css`
    position: absolute;
    inset: 0 auto 0 0;
    background: ${token.colorPrimary};
    border-radius: 2px;
  `,
  progressThumb: css`
    position: absolute;
    top: 50%;
    left: 0;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #fff;
    box-shadow: none;
    transform: translate(-50%, -50%);
    pointer-events: none;
  `,
  timeLabel: css`
    flex: none;
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.92);
    min-width: 70px;
    text-align: right;
    letter-spacing: 0.2px;
  `,
  placeholder: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 透明度走 svg 元素 opacity(group 合成,压平重叠);不要把 alpha 塞进 color —
       IconVideo 是双 stroke 路径,逐路径半透会在重叠处叠出更亮的"叠影"。 */
    color: #fff;
    background: #000;
    transition: opacity 200ms ease-out;
    svg {
      opacity: 0.5;
    }
  `,
  thumbImg: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: #000;
    pointer-events: none;
  `,
}));

function VideoPlayerImpl({
  src,
  autoPlay = false,
  loop = false,
  interactionLight: backgroundInteractionLight = false,
  shouldHandleSpace,
  preview = false,
  exclusive = false,
  maxHeight,
  allowFullscreen = preview,
  onError,
  poster,
  deferVideoThumb,
  videoElRef,
  disabled,
  onDurationChange,
  onDimensions,
  onMountedChange,
  onCaptureFrame,
  captureBusy,
  style,
  className,
}: VideoPlayerProps) {
  const { styles, cx } = useStyles();
  const intl = useIntl();
  const innerRef = useRef<HTMLVideoElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [naturalRatio, setNaturalRatio] = useState(16 / 9);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const onDurationChangeRef = useRef(onDurationChange);
  onDurationChangeRef.current = onDurationChange;
  const onDimensionsRef = useRef(onDimensions);
  onDimensionsRef.current = (width, height) => {
    if (preview && width > 0 && height > 0) setNaturalRatio(width / height);
    onDimensions?.(width, height);
  };
  const onMountedChangeRef = useRef(onMountedChange);
  onMountedChangeRef.current = onMountedChange;
  const [hovered, setHovered] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [ready, setReady] = useState(false);
  const [activated, setActivated] = useState(false);
  const pendingPlayRef = useRef(false);
  // A focused preview must not inherit background canvas rendering optimizations.
  const interactionLight = backgroundInteractionLight && !preview;

  useEffect(() => {
    setReady(false);
    setSeeking(false);
    setNaturalRatio(16 / 9);
    pendingPlayRef.current = false;
  }, [src]);

  const thumb = poster || null;
  // 无 poster(上传视频)→ 缩略图层用 `#t=0.1` 媒体片段解首帧(preload=metadata 不解首帧会黑屏);
  // 首次 hover / 空格 / 点击后才懒挂播放 video。两层各自独立 currentTime,
  // 缩略图恒停首帧不污染播放进度。
  const deferThumb = deferVideoThumb;
  const mounted =
    disabled ||
    ((activated || autoPlay || preview) && !interactionLight) ||
    (interactionLight && innerRef.current !== null);
  const { muted, play, pause, toggleMuted } = useVideoPlayback(
    innerRef,
    mounted && !disabled && !interactionLight,
    src,
    exclusive,
  );

  useEffect(() => {
    if (autoPlay && mounted && !disabled && !interactionLight) play();
  }, [autoPlay, mounted, disabled, interactionLight, src, play]);

  useEffect(() => {
    if (!mounted) {
      setPlaying(false);
      setReady(false);
      setSeeking(false);
    }
    onMountedChangeRef.current?.(mounted);
  }, [mounted]);

  const setRefs = useCallback(
    (el: HTMLVideoElement | null) => {
      innerRef.current = el;
      if (videoElRef) {
        (videoElRef as { current: HTMLVideoElement | null }).current = el;
      }
    },
    [videoElRef],
  );

  useEffect(() => {
    if (!mounted) return;
    const v = innerRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    // loadeddata = 首帧已解码可绘制 → 比 canplay 更早,空闲缩略图(无 poster)靠它显首帧
    const onCanPlay = () => setReady(true);
    const onDur = () => {
      const d = Number.isFinite(v.duration) ? v.duration : 0;
      const w = v.videoWidth > 0 ? v.videoWidth : undefined;
      const h = v.videoHeight > 0 ? v.videoHeight : undefined;
      if (d > 0) {
        onDurationChangeRef.current?.(d);
      }
      if (w && h) {
        onDimensionsRef.current?.(w, h);
      }
      if (d > 0 || (w && h)) {
        rememberVideoMeta(src, {
          duration: d > 0 ? d : undefined,
          width: w,
          height: h,
        });
      }
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('loadedmetadata', onDur);
    v.addEventListener('durationchange', onDur);
    v.addEventListener('loadeddata', onCanPlay);
    v.addEventListener('canplay', onCanPlay);
    setPlaying(!v.paused);
    if (v.readyState >= 2) setReady(true);
    if (v.videoWidth > 0 && v.videoHeight > 0) {
      onDimensionsRef.current?.(v.videoWidth, v.videoHeight);
    }
    // 缓存命中 → 立即回填时长(chip「Ns」不必等本次 metadata 重新加载)
    const cached = videoMetaCache.get(src);
    if (cached?.duration && cached.duration > 0) {
      onDurationChangeRef.current?.(cached.duration);
    }
    if (cached?.width && cached?.height) {
      onDimensionsRef.current?.(cached.width, cached.height);
    }
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('loadedmetadata', onDur);
      v.removeEventListener('durationchange', onDur);
      v.removeEventListener('loadeddata', onCanPlay);
      v.removeEventListener('canplay', onCanPlay);
    };
  }, [src, mounted]);

  const playMountedVideo = useCallback(() => {
    if (disabled || interactionLight) return;
    const v = innerRef.current;
    if (!v) return;
    pendingPlayRef.current = false;
    play();
  }, [disabled, interactionLight, play]);

  // 首次触发后才挂载真实 video。默认只加载 poster 缩略图,避免大画布同时挂大量 video。
  useEffect(() => {
    if (disabled || !mounted || interactionLight || !pendingPlayRef.current) {
      return;
    }
    playMountedVideo();
  }, [mounted, disabled, interactionLight, playMountedVideo]);

  useMediaProgress(
    innerRef,
    ({ ratio }) => {
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${ratio})`;
      if (thumbRef.current) thumbRef.current.style.left = `${ratio * 100}%`;
    },
    mounted && !interactionLight && !disabled,
    src,
  );

  const togglePlay = useCallback(() => {
    const v = innerRef.current;
    if (!v) {
      pendingPlayRef.current = true;
      setActivated(true);
      return;
    }
    if (v.paused) {
      pendingPlayRef.current = false;
      play();
    } else {
      pendingPlayRef.current = false;
      pause();
    }
  }, [play, pause]);

  useEffect(() => {
    if (disabled || interactionLight || !shouldHandleSpace) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        (event.code !== 'Space' && event.key !== ' ') ||
        event.repeat ||
        !shouldHandleSpace(event)
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      togglePlay();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [disabled, interactionLight, shouldHandleSpace, togglePlay]);

  useEffect(() => {
    if (!allowFullscreen) return;
    const sync = () =>
      setFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [allowFullscreen]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => {});
    } else if (wrapRef.current?.requestFullscreen) {
      void wrapRef.current.requestFullscreen().catch(() => {});
    } else {
      (
        innerRef.current as
          | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
          | null
      )?.webkitEnterFullscreen?.();
    }
  };

  const seekTo = useCallback((ratio: number) => {
    const v = innerRef.current;
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    const r = Math.max(0, Math.min(1, ratio));
    try {
      v.currentTime = v.duration * r;
    } catch {}
  }, []);

  const handleSeekPointer = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const track = e.currentTarget;
      const rect = track.getBoundingClientRect();
      seekTo((e.clientX - rect.left) / rect.width);
      setSeeking(true);
      track.setPointerCapture(e.pointerId);
      const onMove = (ev: PointerEvent) => {
        seekTo((ev.clientX - rect.left) / rect.width);
      };
      const onUp = (ev: PointerEvent) => {
        setSeeking(false);
        try {
          track.releasePointerCapture(ev.pointerId);
        } catch {}
        track.removeEventListener('pointermove', onMove);
        track.removeEventListener('pointerup', onUp);
        track.removeEventListener('pointercancel', onUp);
      };
      track.addEventListener('pointermove', onMove);
      track.addEventListener('pointerup', onUp);
      track.addEventListener('pointercancel', onUp);
    },
    [seekTo],
  );

  const showChrome = !disabled && (preview || hovered || playing || seeking);

  const handlePointerEnter = useCallback(() => {
    setHovered(true);
    if (preview) return;
    if (disabled || interactionLight) return;
    if (!mounted) {
      pendingPlayRef.current = true;
      setActivated(true);
      return;
    }
    playMountedVideo();
  }, [preview, disabled, interactionLight, mounted, playMountedVideo]);

  const handlePointerLeave = useCallback(() => {
    setHovered(false);
    if (preview) return;
    pendingPlayRef.current = false;
    if (seeking) return;
    pause();
  }, [preview, seeking, pause]);

  return (
    <div
      ref={wrapRef}
      className={cx(styles.wrap, className)}
      data-video-player="unified"
      tabIndex={preview && !disabled ? 0 : undefined}
      onKeyDown={(event) => {
        if (
          preview &&
          event.target === event.currentTarget &&
          (event.code === 'Space' || event.key === ' ')
        ) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) togglePlay();
        }
      }}
      data-preview={preview ? 'true' : 'false'}
      style={
        preview
          ? {
              aspectRatio: naturalRatio,
              height: 'auto',
              ...(maxHeight
                ? {
                    maxHeight,
                    width: `min(100%, calc(${maxHeight}px * ${naturalRatio}))`,
                  }
                : {}),
              ...style,
            }
          : style
      }
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {/* 缩略图层:有 poster → img;无 poster 但有 src → 用首帧
          (`#t=0.1` 媒体片段让 preload=metadata 也解出第一帧,不依赖后端封面 / CORS),
          顺带从其 loadedmetadata 读真实宽高做比例自适应;都没有 → icon 占位。 */}
      {preview && !thumb ? null : thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          decoding="async"
          className={styles.thumbImg}
        />
      ) : src && !deferThumb ? (
        <video
          src={`${src}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden
          data-ready="true"
          className={styles.thumbImg}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth > 0 && v.videoHeight > 0) {
              onDimensionsRef.current?.(v.videoWidth, v.videoHeight);
            }
            // 顺带探测真实时长(计费/报价按真实时长,不靠配置值)
            const dur = Number.isFinite(v.duration) ? v.duration : 0;
            if (dur > 0) onDurationChangeRef.current?.(dur);
            if (dur > 0 || (v.videoWidth > 0 && v.videoHeight > 0)) {
              rememberVideoMeta(src, {
                duration: dur > 0 ? dur : undefined,
                width: v.videoWidth > 0 ? v.videoWidth : undefined,
                height: v.videoHeight > 0 ? v.videoHeight : undefined,
              });
            }
          }}
        >
          <track kind="captions" />
        </video>
      ) : (
        <div className={styles.placeholder}>
          <VideoCameraOutlined style={{ fontSize: 24 }} />
        </div>
      )}

      {/* 懒挂 video:仅 mounted 时渲染。
          - 不设 crossOrigin:本播放器不做 canvas 抽帧(抽帧在 VideoTrimOverlay),
            设了反而强制要求 MinIO 视频 CORS,缺失时 hover 播放被浏览器拦住卡住
          - 不传 controls,完全自渲 chrome
          - 不挂 onClick:点击交给 xyflow 默认(选中节点 / 拖动),与画布其他节点点击语义一致
          - 播放由 hover / 空格 / 点击触发,不靠浏览器 autoPlay 属性
          - controlsList:部分浏览器兜底(Safari 只看 disablePictureInPicture) */}
      {mounted && (
        <video
          ref={setRefs}
          src={src}
          poster={poster}
          muted={muted}
          playsInline
          preload="metadata"
          autoPlay={false}
          loop={loop}
          onError={onError}
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          data-ready={ready ? 'true' : 'false'}
        >
          <track kind="captions" />
        </video>
      )}

      {!disabled && !interactionLight && (
        <button
          type="button"
          className={cx(styles.muteChip, 'nodrag', 'nopan')}
          data-show={showChrome}
          onClick={(e) => {
            e.stopPropagation();
            toggleMuted();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          title={intl.formatMessage({
            id: muted ? 'canvasOps.video.unmute' : 'canvasOps.video.mute',
          })}
          aria-label={intl.formatMessage({
            id: muted ? 'canvasOps.video.unmute' : 'canvasOps.video.mute',
          })}
        >
          {muted ? (
            <AudioMutedOutlined style={{ fontSize: 10 }} />
          ) : (
            <SoundOutlined style={{ fontSize: 10 }} />
          )}
        </button>
      )}

      {onCaptureFrame && !interactionLight && (
        <div
          className={cx(styles.captureChip, 'nodrag', 'nopan')}
          data-show={showChrome}
          data-busy={captureBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (!captureBusy) onCaptureFrame();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          title={intl.formatMessage({ id: 'canvasOps.video.capture' })}
        >
          <CameraOutlined style={{ fontSize: 11 }} />
        </div>
      )}

      {!interactionLight && (
        <div
          className={cx(styles.chrome, 'nodrag', 'nopan')}
          data-show={showChrome}
        >
          <button
            type="button"
            className={styles.playBtn}
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title={intl.formatMessage({
              id: playing ? 'canvasOps.video.pause' : 'canvasOps.video.play',
            })}
            aria-label={intl.formatMessage({
              id: playing ? 'canvasOps.video.pause' : 'canvasOps.video.play',
            })}
          >
            {playing ? (
              <PauseOutlined style={{ fontSize: 12 }} />
            ) : (
              <PlaySquareOutlined style={{ fontSize: 12 }} />
            )}
          </button>
          <div
            className={styles.progressTrack}
            onPointerDown={handleSeekPointer}
          >
            <div className={styles.progressLine}>
              <div
                ref={fillRef}
                className={styles.progressFill}
                style={{
                  width: '100%',
                  transformOrigin: 'left',
                  transform: 'scaleX(0)',
                }}
              />
            </div>
            <div ref={thumbRef} className={styles.progressThumb} />
          </div>
          <MediaTime
            key={src}
            mediaRef={innerRef}
            source={src}
            enabled={mounted && !interactionLight && !disabled}
            className={styles.timeLabel}
          />
          {allowFullscreen && (
            <button
              type="button"
              className={styles.playBtn}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                toggleFullscreen();
              }}
              aria-label={intl.formatMessage({
                id: fullscreen
                  ? 'canvasOps.video.exitFullscreen'
                  : 'canvasOps.video.fullscreen',
                defaultMessage: fullscreen ? '退出全屏' : '全屏',
              })}
            >
              {fullscreen ? (
                <CompressOutlined style={{ fontSize: 12 }} />
              ) : (
                <FullscreenOutlined style={{ fontSize: 12 }} />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const VideoPlayer = memo(VideoPlayerImpl);
export default VideoPlayer;
